// The books at a glance: cash, receivables, payables, tax owing and this month's profit, straight
// from the posted lines, and what needs a person today.

import { IntegrityLine } from './Integrity.tsx';
import { useSalesTaxCodes } from './Tax.tsx';
import type { BooksProps } from './props.ts';
import {
  cashNote,
  isBooksRequest,
  money,
  monthName,
  monthOf,
  previousMonth,
  todayIn,
  total,
} from './model.ts';
import type {
  ApAging,
  Approval,
  ArAging,
  BankAccount,
  CloseChecks,
  DueReminder,
  ProfitAndLoss,
  TaxReturn,
  VendorBill,
} from './types.ts';
import { Money, useQuery } from './ui.tsx';

function Tile({ label, cents, note }: { label: string; cents: number | null; note?: string }) {
  return (
    <div className="sheet tile">
      <span className="tile-label">{label}</span>
      {cents === null ? (
        <span className="ph-pebble" aria-hidden="true" />
      ) : (
        <Money cents={cents} className="big" />
      )}
      {note && <span className="muted small">{note}</span>}
    </div>
  );
}

/** What the overview reads, and what needs a person today: Riverstone's tiles and Blueprint's page (OverviewPage.tsx). */
export function useBooksOverview() {
  const today = todayIn();
  const [mStart, mEnd] = monthOf(today);
  const [pStart, pEnd] = previousMonth(today);
  const banks = useQuery<{ bankAccounts: BankAccount[]; undepositedCents: number }>('bankAccounts.list', {});
  const ar = useQuery<ArAging>('receivables.aging', {});
  const ap = useQuery<ApAging>('payables.aging', {});
  const pl = useQuery<ProfitAndLoss>('reports.profitAndLoss', { from: mStart, to: today });
  const codes = useSalesTaxCodes();
  const gst = useQuery<TaxReturn>('tax.computeReturn', {
    taxCode: 'GST',
    periodStart: pStart,
    periodEnd: pEnd,
  });
  const pst = useQuery<TaxReturn>('tax.computeReturn', {
    taxCode: 'PST',
    periodStart: pStart,
    periodEnd: pEnd,
  });
  const reminders = useQuery<{ reminders: DueReminder[] }>('receivables.dueReminders', {});
  const drafts = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'draft' });
  const asks = useQuery<{ approvals: Approval[] }>('approvals.list', { status: 'pending' });
  const close = useQuery<CloseChecks>('periods.closeChecks', { date: pEnd });
  // Postings that wait on an account nobody mapped (QA-071): told once per account.
  const alerts = useQuery<{ notifications: { kind: string; title: string }[] }>('notifications.list', {
    unreadOnly: true,
    limit: 50,
  });
  const waiting = alerts.data?.notifications.filter((n) => n.kind === 'posting.missing_account') ?? [];

  const cash = banks.data ? total(banks.data.bankAccounts, (b) => b.bookBalanceCents) : null;
  const unmatched = banks.data ? total(banks.data.bankAccounts, (b) => b.unmatchedCount) : 0;
  const overdue = ar.data
    ? total(
        ar.data.rows.filter((r) => r.daysPastDue > 0),
        (r) => r.balanceCents,
      )
    : 0;
  const booksAsks = asks.data?.approvals.filter((a) => isBooksRequest(a.action)) ?? [];
  const owing = (r: TaxReturn | null) =>
    r ? (r.filed ? r.figures.netCents - r.filed.remittedCents : r.figures.netCents) : null;

  const todo: { what: string; why: string; go: Parameters<BooksProps['go']>[0] }[] = [];
  for (const n of waiting)
    todo.push({
      what: n.title,
      why: 'Choose one in Books setup; the waiting postings then go through on their own',
      go: 'setup',
    });
  if (unmatched)
    todo.push({
      what: `${unmatched} bank line${unmatched === 1 ? '' : 's'} to review`,
      why: 'Accept a match, categorise, or teach a rule',
      go: 'banking',
    });
  if (booksAsks.length)
    todo.push({
      what: `${booksAsks.length} agent request${booksAsks.length === 1 ? '' : 's'} waiting`,
      why: 'Nothing moves until a person decides',
      go: 'approvals',
    });
  if (reminders.data?.reminders.length)
    todo.push({
      what: `${reminders.data.reminders.length} payment reminder${reminders.data.reminders.length === 1 ? '' : 's'} due today`,
      why: 'They go out at 10:00 a.m. on their own; send one now from Receivables',
      go: 'receivables',
    });
  if (drafts.data?.bills.length)
    todo.push({
      what: `${drafts.data.bills.length} vendor bill${drafts.data.bills.length === 1 ? '' : 's'} to approve`,
      why: drafts.data.bills
        .slice(0, 2)
        .map((b) => `${b.number} ${b.vendorName}`)
        .join(' · '),
      go: 'payables',
    });
  for (const [code, r] of [
    ['GST', gst.data],
    ['PST', pst.data],
  ] as const)
    if (r && codes?.includes(code) && !r.filed && r.figures.entryCount > 0)
      todo.push({
        what: `File ${monthName(pStart)} ${code}`,
        why: `Net ${code} owing for the month`,
        go: 'tax',
      });
  // A month with nothing posted (before the company started, say) has nothing to close (QA-340).
  if (close.data && close.data.period.status === 'open' && close.data.posted)
    todo.push({
      what: `Close ${monthName(pStart)}`,
      why: close.data.ready
        ? 'Every check passes'
        : `${close.data.checks.filter((c) => !c.ok && c.blocking).length} check(s) to clear first`,
      go: 'ledger',
    });

  return { today, mStart, mEnd, pStart, banks, ar, ap, pl, codes, gst, pst, cash, overdue, owing, todo };
}

export function Overview({ go, timeZone, held }: BooksProps) {
  const { today, mStart, mEnd, pStart, banks, ar, ap, pl, codes, gst, pst, cash, overdue, owing, todo } =
    useBooksOverview();
  return (
    <div className="books-page">
      <div className="tiles">
        <Tile label="Cash in bank" cents={cash} note={banks.data ? cashNote(banks.data) : undefined} />
        <Tile
          label="Receivables"
          cents={ar.data?.openInvoicesCents ?? null}
          note={ar.data ? `Overdue ${money(overdue)}` : undefined}
        />
        <Tile
          label="Payables"
          cents={ap.data?.openBillsCents ?? null}
          note={
            ap.data
              ? `${ap.data.rows.length} approved bill${ap.data.rows.length === 1 ? '' : 's'} to pay`
              : undefined
          }
        />
        {/* only the taxes the company charges (its invoices' zone), as Getting started says */}
        {codes?.includes('GST') && (
          <Tile label="GST owing" cents={owing(gst.data)} note={monthName(pStart)} />
        )}
        {codes?.includes('PST') && (
          <Tile label="PST owing" cents={owing(pst.data)} note={monthName(pStart)} />
        )}
        <Tile
          label="Profit"
          cents={pl.data?.netProfitCents ?? null}
          note={
            pl.data
              ? `${monthName(mStart)} · income ${money(pl.data.incomeCents)} to ${mEnd > today ? 'today' : 'month end'}`
              : undefined
          }
        />
      </div>
      <IntegrityLine go={go} timeZone={timeZone} held={held} />
      <section className="sheet panel" aria-label="Needs attention">
        <div className="panel-head">
          <h2>Needs attention</h2>
          <span className="chip">{todo.length} open</span>
        </div>
        {todo.length === 0 ? (
          <p className="muted empty">Nothing waiting. The books are up to date.</p>
        ) : (
          <ul className="todo">
            {todo.map((t) => (
              <li key={t.what}>
                <button className="todo-row" onClick={() => go(t.go)}>
                  <span>
                    <strong>{t.what}</strong>
                    <span className="muted small">{t.why}</span>
                  </span>
                  <span aria-hidden="true">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
